import React, { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import rpc, { buildRequest, signoutAndRedirectToIndex } from 'lib/rpc'
import { getWeekStart, getWeekDays, getMonthsInWeek, hasWeekendEvents, computeHourRange } from './utils'
import WeekNavigation from './WeekNavigation'
import WeekGrid from './WeekGrid'
import MobileAgenda from './MobileAgenda'

export default function Timeline() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()))
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const [showAll, setShowAll] = useState(() => localStorage.getItem('schedule_showAll') === 'true')

  const fetchSchedule = useCallback(async () => {
    setLoading(true)
    try {
      const months = getMonthsInWeek(weekStart)
      const requests = months.map(({ year, month }) =>
        rpc.post('', buildRequest('admin.lessons.schedule', { year, month, show_all: showAll }))
      )
      const responses = await Promise.all(requests)

      // Merge results, deduplicate by lesson id
      const seen = new Set()
      const merged = []
      for (const response of responses) {
        const result = response.data.result
        if (result.message) {
          if (result.code === 5) {
            signoutAndRedirectToIndex()
            return
          }
          console.error(result)
          continue
        }
        for (const lesson of result) {
          if (!seen.has(lesson.id)) {
            seen.add(lesson.id)
            merged.push({
              id: lesson.id,
              groupId: lesson.group.id,
              from: new Date(lesson.scheduled_on),
              to: new Date(new Date(lesson.scheduled_on).getTime() + 60 * 60 * 1000),
              group: lesson.group.name,
              teacher: lesson.teacher.first_name,
            })
          }
        }
      }
      setEvents(merged)
    } catch (err) {
      console.error('Failed to fetch schedule:', err)
    } finally {
      setLoading(false)
    }
  }, [weekStart, showAll])

  useEffect(() => {
    fetchSchedule()
  }, [fetchSchedule])

  useEffect(() => {
    localStorage.setItem('schedule_showAll', showAll)
  }, [showAll])

  const includeWeekend = hasWeekendEvents(events)
  const days = getWeekDays(weekStart, includeWeekend)
  const { hourStart, hourEnd } = computeHourRange(events)

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mb: 1 }}>
        <WeekNavigation weekStart={weekStart} onWeekChange={setWeekStart} />
        <Stack direction="row" alignItems="center">
          <Typography variant="body2" color="text.secondary">Only my Lessons</Typography>
          <Switch
            size="small"
            checked={!showAll}
            aria-label="Only my Lessons"
            onChange={(e) => setShowAll(!e.target.checked)}
          />
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <MobileAgenda days={days} events={events} />
      ) : (
        <WeekGrid days={days} events={events} hourStart={hourStart} hourEnd={hourEnd} />
      )}
    </Box>
  )
}
